import { Container, Sprite } from "pixi.js";
import { SplashScreen } from "@westpierstudio/luma/game/SplashScreen";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { GameServices } from "./GameServices";
import { KSGLogo } from "components/KSGLogo";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { Text } from "pixi.js";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";

export class DragonsVsPandasSplashScreen extends SplashScreen {
    private spec!: Record<string, any>;
    private backgroundContainer!: Container;
    private foregroundContainer!: Container;
    private ksgLogo!: KSGLogo;
    private logo!: Sprite;
    private doNotShowContainer!: Container;
    private doNotShowTick!: Sprite;
    private doNotShowBox! : {normal: Sprite, over: Sprite};
    private continueButton!: Container;
    private continueSprites! : {normal: Sprite, over: Sprite};
    private messageTextFeatures!: Text | AdvancedText;
    private messageContainer!: BoundedContainer;

    private backgroundSections!: {
        topLeft: Sprite;
        topRight: Sprite;
        bottomLeft: Sprite;
        bottomRight: Sprite;
    };

    constructor(protected override services: GameServices) {
        super(services);
    }

    override setup() {

        if (!this._hasSetup) {

            const {factory, resources} = this.services;

            const spec = resources.getSpec(`splashscreen`);
            this.setupBackground();

            this.foregroundContainer = factory.createContainer(spec.foregroundContainer, this);

            this.ksgLogo = this.addChild(new KSGLogo(this.services, this.services.splashscreenLayout));
            this.on(`added`, () => this.ksgLogo.resize());

            this.logo = factory.createSprite(spec.splashLogo, this.foregroundContainer);

            this.messageContainer = new BoundedContainer(spec.maxBounds.message);
            this.foregroundContainer.addChild(this.messageContainer);
            this.messageTextFeatures = factory.createText(resources.getMessage(`splashscreenFeatures`), spec.messageTextFeatures, this.messageContainer);

            this.setupDoNotShowContainer(spec.doNotShowContainer);
            this.setupContinueButton(spec.continueButton);

            this.backgroundSections = {
                topLeft: factory.createSprite(spec.splashBackground.topLeft, this.backgroundContainer),
                topRight: factory.createSprite(spec.splashBackground.topRight, this.backgroundContainer),
                bottomLeft: factory.createSprite(spec.splashBackground.bottomLeft, this.backgroundContainer),
                bottomRight: factory.createSprite(spec.splashBackground.bottomRight, this.backgroundContainer),
            };

            this.spec = spec;
            this.resize();
        }
    }

    override onContinueClick() {

        this.continueButton.interactive = false;
        this.continueButton.visible = false;
        this.doNotShowContainer.visible = false;

        this.onSplashScreenContinue(this.doNotShowTick.visible);
    }

    override setProgress(_progress: number): void {
    }

    override hideLoadingBar(): void {
    }

    private setupBackground():void {

        const {factory, splashscreenLayout} = this.services;

        this.backgroundContainer = factory.createContainer({}, this);

        this.resize();
        splashscreenLayout.onResize.add(() => this.resize());
    }

    private setupDoNotShowContainer(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        this.doNotShowContainer = factory.createContainer({anchor: 0.5}, this.foregroundContainer);

        this.doNotShowBox = {
            normal: factory.createSprite(spec.splashCheckBox, this.doNotShowContainer),
            over: factory.createSprite(spec.splashCheckBoxOver, this.doNotShowContainer)
        };

        this.doNotShowTick = factory.createSprite(spec.splashTick, this.doNotShowContainer);
        const text = factory.createText(resources.getMessage(`splashDoNotShow`), spec.doNotShowText, this.doNotShowContainer);
        ImplementBoundedContainer(text, spec.maxBounds.doNotShowText, true);

        this.doNotShowContainer.interactive = this.doNotShowContainer.buttonMode = true;

        this.doNotShowContainer
        .addListener(`click`, this.onDoNotShowClick, this)
        .addListener(`tap`, this.onDoNotShowClick, this)
        .addListener(`mouseover`, this.showDoNotShowOver, this)
        .addListener(`pointerover`, this.showDoNotShowOver, this)
        .addListener(`mouseout`, this.hideDoNotShowOver, this)
        .addListener(`pointerout`, this.hideDoNotShowOver, this)
        .addListener(`mousedown`, this.hideDoNotShowOver, this)
        .addListener(`pointerdown`, this.hideDoNotShowOver, this);
    }

    private onDoNotShowClick(): void {
        this.doNotShowTick.visible = !this.doNotShowTick.visible;
    }

    private showDoNotShowOver(): void {
        this.doNotShowBox.over.visible = true;
    }

    private hideDoNotShowOver(): void {
        this.doNotShowBox.over.visible = false;
    }

    private setupContinueButton(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        this.continueButton = factory.createContainer({anchor: 0.5}, this.foregroundContainer);

        this.continueSprites = {
            normal: factory.createSprite(spec.splashContinue, this.continueButton),
            over: factory.createSprite(spec.splashContinueOver, this.continueButton)
        };

        const text = factory.createText(resources.getMessage(`splashContinue`), spec.continueText, this.continueButton);
        ImplementBoundedContainer(text, spec.maxTextBounds, true);

        this.continueButton.interactive = this.continueButton.buttonMode = true;

        this.continueButton
        .addListener(`click`, this.onContinueClick, this)
        .addListener(`tap`, this.onContinueClick, this)
        .addListener(`mouseover`, this.showContinueOver, this)
        .addListener(`pointerover`, this.showContinueOver, this)
        .addListener(`mouseout`, this.hideContinueOver, this)
        .addListener(`pointerout`, this.hideContinueOver, this)
        .addListener(`mousedown`, this.hideContinueOver, this)
        .addListener(`pointerdown`, this.hideContinueOver, this);
    }

    private showContinueOver(): void {
        this.continueSprites.over.visible = true;
    }

    private hideContinueOver(): void {
        this.continueSprites.over.visible = false;
    }

    private resize(): void {

        const layout = this.services.splashscreenLayout as DefaultLayout;

        const bounds = layout.getScreenBoundaryInBucketSpace();
        const {width, height, left, top} = bounds;

        if (width > height) {
            this.backgroundContainer.width = width;
            this.backgroundContainer.scale.y = this.backgroundContainer.scale.x;
            this.backgroundContainer.x = left + (width - this.backgroundContainer.width) / 2;
            this.backgroundContainer.y = top + (height - this.backgroundContainer.height) / 2;
        } else {
            this.backgroundContainer.height = height;
            this.backgroundContainer.scale.x = this.backgroundContainer.scale.y;
            this.backgroundContainer.x = (width - this.backgroundContainer.width) / 2;
            this.backgroundContainer.y = top;
        }

        if (this.spec) {

            const {factory} = this.services;

            this.ksgLogo.resize();

            if (layout.orientation === `landscape`) {
                factory.applySpec(this.doNotShowContainer, this.spec.doNotShowContainer.landscape);
                factory.applySpec(this.continueButton, this.spec.continueButton.landscape);

                factory.applySpec(this.messageTextFeatures, this.spec.messageLayouts.landscape);
                this.messageTextFeatures.style = this.spec.messageLayouts.landscape.style;

                this.messageContainer.y = this.spec.messageLayouts.landscape.containerY;
            } else {
                factory.applySpec(this.doNotShowContainer, this.spec.doNotShowContainer.portrait);
                factory.applySpec(this.continueButton, this.spec.continueButton.portrait);

                factory.applySpec(this.messageTextFeatures, this.spec.messageLayouts.portrait);
                this.messageTextFeatures.style = this.spec.messageLayouts.portrait.style;

                this.messageContainer.y = this.spec.messageLayouts.portrait.containerY;
            }

            this.messageContainer.update();
        }
    }
}